namespace $ {

	type $shm_hitalama_map_loading_profile_bucket = { ms: number, calls: number }
	type $shm_hitalama_map_loading_profile_frame = {
		start: number, children: number, widget: string, kind: string
		blocker?: $shm_hitalama_map_loading_profile_wait
	}
	type $shm_hitalama_map_loading_profile_wait = {
		from: number, until?: number, resumed?: number, label: string, widgets: Set< string >
	}

	/** Exclusive JS slices from the existing wire diagnostic hook. No reactive reads in the hook. */
	export class $shm_hitalama_map_loading_profile extends $shm_hitalama_wire_measure {
		private stack: $shm_hitalama_map_loading_profile_frame[] = []
		private owners = new WeakMap< object, string >()
		private fibers = new WeakMap< object, string >()
		private samples = new Map< string, Map< string, $shm_hitalama_map_loading_profile_bucket > >()
		private io = new Map< string, Map< string, $shm_hitalama_map_loading_profile_bucket > >()
		private promises = new WeakSet< object >()
		private waiting = new WeakMap< object, $shm_hitalama_map_loading_profile_wait >()
		private blocked = new WeakMap< object, $shm_hitalama_map_loading_profile_wait >()
		private waits: $shm_hitalama_map_loading_profile_wait[] = []
		private slices: { from: number, until: number, widget: string }[] = []
		private scope: string | null = null
		private scope_from = 0
		private dropped = 0
		private reported = new WeakMap< $shm_hitalama_process_observation, Set< string > >()
		private stopped = false
		constructor( readonly clock = ()=> performance.now() ) { super() }

		bind( host: object, widget: string ) { this.owners.set( host, widget ) }

		private switch_scope( scope: string | null, now: number ) {
			if( this.scope === scope ) return
			if( this.scope !== null && now > this.scope_from ) {
				if( this.slices.length < 8192 ) this.slices.push({ from: this.scope_from, until: now, widget: this.scope })
				else ++this.dropped
			}
			this.scope = scope; this.scope_from = now
		}

		private owner( host: unknown ): string {
			if( !host || typeof host !== 'object' ) return ''
			// Owning edges are plain WeakMap entries: do not call model/view channels here.
			for( let owner: object | undefined = host; owner; owner = $mol_owning_map.get( owner ) ) {
				const widget = this.owners.get( owner )
				if( widget ) { this.owners.set( host, widget ); return widget }
			}
			return ''
		}

		enter( fiber: $mol_wire_fiber< any, any, any > ) {
			const now = this.clock()
			const blocked = this.blocked.get( fiber )
			if( blocked?.until !== undefined && blocked.resumed === undefined ) blocked.resumed = now
			const parent = this.stack[ this.stack.length - 1 ]
			const widget = this.owner( fiber.host ) || parent?.widget || this.fibers.get( fiber ) || ''
			if( widget ) this.fibers.set( fiber, widget )
			const method = fiber.task.name.trim()
			const land = fiber.host instanceof $giper_baza_land
			const baza = land || fiber.host instanceof $giper_baza_pawn || fiber.host instanceof $giper_baza_mine_temp
			let kind = widget ? 'prepare' : ''
			if( land && method === 'diff_apply' ) kind = 'apply'
			else if( land && method === 'sand_decode' ) kind = 'decode'
			else if( baza ) kind = parent?.kind === 'apply' || parent?.kind === 'decode' ? parent.kind : 'crdt'
			else if( widget && fiber.host instanceof $mol_view && /^(dom_tree|dom_node|dom_node_actual|render)$/.test( method ) ) kind = 'dom'
			else if( parent?.kind === 'apply' || parent?.kind === 'decode' ) kind = parent.kind
			const frame: $shm_hitalama_map_loading_profile_frame = { start: now, children: 0, widget, kind }
			this.switch_scope( widget, now )
			this.stack.push( frame )
			return frame
		}

		leave( token: unknown, fiber: $mol_wire_fiber< any, any, any > ) {
			const frame = token as $shm_hitalama_map_loading_profile_frame
			const now = this.clock()
			const elapsed = Math.max( 0, now - frame.start )
			this.stack.pop()
			const parent = this.stack[ this.stack.length - 1 ]
			if( parent ) parent.children += elapsed
			this.switch_scope( parent?.widget ?? null, now )
			if( !this.stopped && $mol_promise_like( fiber.cache ) ) {
				let wait = frame.blocker ?? this.waiting.get( fiber.cache )
				if( !wait && this.waits.length < 1024 ) {
					const method = fiber.task.name.trim() || '(anonymous)'
					const host = fiber.host?.constructor?.name ?? ''
					const label = method === 'units_load' ? 'IndexedDB: чтение операций'
						: method === 'ball_load' ? 'IndexedDB: чтение файлового блока'
						: `${ host.replace( /^\$/, '' ) }.${ method }()`.slice( 0, 140 )
					wait = { from: now, label, widgets: new Set }
					this.waits.push( wait )
					const record = wait
					const finish = ()=> { if( !this.stopped ) record.until = this.clock() }
					fiber.cache.then( finish, finish )
				} else if( !wait ) ++this.dropped
				if( wait ) {
					wait.widgets.add( frame.widget )
					this.waiting.set( fiber.cache, wait )
					this.blocked.set( fiber, wait )
					if( parent ) parent.blocker = wait
				}
			}
			const method = fiber.task.name.trim()
			if( !this.stopped && fiber.host instanceof $giper_baza_mine_temp && /^(units_load|ball_load)$/.test( method ) && $mol_promise_like( fiber.cache ) && !this.promises.has( fiber.cache ) ) {
				this.promises.add( fiber.cache )
				if( this.io.has( frame.widget ) || this.io.size < 201 ) {
					let samples = this.io.get( frame.widget )
					if( !samples ) this.io.set( frame.widget, samples = new Map )
					let sample = samples.get( method )
					if( !sample ) samples.set( method, sample = { ms: 0, calls: 0 } )
					const bucket = sample
					const finish = ()=> { if( !this.stopped ) { bucket.ms += this.clock() - frame.start; ++bucket.calls } }
					// Observe settlement, never replace/wrap the Promise held by wire.
					fiber.cache.then( finish, finish )
				}
			}
			if( this.stopped || !frame.kind ) return
			// Bound attribution even for a pathological map. The empty key is shared work.
			if( !this.samples.has( frame.widget ) && this.samples.size >= 201 ) return
			let samples = this.samples.get( frame.widget )
			if( !samples ) this.samples.set( frame.widget, samples = new Map )
			let sample = samples.get( frame.kind )
			if( !sample ) samples.set( frame.kind, sample = { ms: 0, calls: 0 } )
			sample.ms += Math.max( 0, elapsed - frame.children )
			++sample.calls
		}

		report( stage: $shm_hitalama_process_observation, widget = '', wall_ms?: number, started_tick?: number, finished = true ) {
			const labels: Record< string, string > = {
				apply: 'CRDT: применение операций · JS',
				decode: 'CRDT: распаковка значений · JS',
				crdt: 'CRDT: чтение полей и списков · JS',
				prepare: 'Подготовка содержимого · JS',
				dom: 'DOM: сборка и обновление узлов · JS',
			}
			let total = 0
			for( const [ key, sample ] of this.samples.get( widget ) ?? [] ) {
				total += sample.ms
				const child = stage.step( 'js-' + key, labels[key] )
				child.done()
				child.process.data_update({ measured_ms: sample.ms, display_order: Object.keys( labels ).indexOf( key ), result: { calls: sample.calls, timing: 'Сумма синхронных участков; вложенные измеренные вызовы вычтены. Не включает ожидание Promise. calls — число измеренных обновлений fiber, не число CRDT-операций.' } })
			}
			if( wall_ms !== undefined ) {
				const until = started_tick === undefined ? this.clock() : started_tick + wall_ms
				const from = started_tick ?? until - wall_ms
				const ranges = ( slices: typeof this.slices )=> slices.map( s => [ s.from, s.until ] as const )
				const busy = ranges( this.slices )
				const other = ranges( this.slices.filter( s => s.widget !== widget ) )
				const waits = this.waits.filter( w => w.widgets.has( widget ) )
				const pending = waits.map( w => [ w.from, w.until ?? until ] as const )
				const resumed = waits.filter( w => w.until !== undefined && w.resumed !== undefined ).map( w => [ w.until!, w.resumed! ] as const )
				let rest = Math.max( 0, wall_ms - total )
				const metric = ( key: string, label: string, ms: number, result: unknown )=> {
					let seen = this.reported.get( stage )
					if( !seen ) this.reported.set( stage, seen = new Set )
					if( !ms && key !== 'between' && !seen.has( key ) ) return
					seen.add( key )
					const part = stage.step( key, label )
					if( key !== 'promise-wait' ) part.done()
					part.process.data_update({ measured_ms: ms, display_order: 10 + [ 'other-js', 'promise-wait', 'resume-gap', 'between' ].indexOf( key ), result })
					return part
				}
				const take = ( ms: number )=> { const value = Math.min( rest, ms ); rest -= value; return value }
				metric( 'other-js', 'Другая работа вкладки · JS', take( $shm_hitalama_map_loading_profile_interval( other, [], from, until ) ), { timing: 'Наблюдавшиеся синхронные wire-вызовы вне этого виджета. Не всё CPU-время вкладки; пересечения с Promise учтены здесь, не дважды.' } )
				const wait_result = { observed: waits.length, pending: waits.filter( w => w.until === undefined ).length, timing: 'Объединение наблюдённых интервалов Promise, за вычетом измеренных JS-участков вкладки. Не является доказательством критического пути. Вложенные причины могут пересекаться; не складывать их.' }
				const wait_stage = metric( 'promise-wait', 'Незавершённые Promise · без JS', take( $shm_hitalama_map_loading_profile_interval( pending, busy, from, until ) ), wait_result )
				const causes = new Map< string, typeof pending >()
				for( const wait of waits ) {
					const list = causes.get( wait.label ) ?? []
					list.push([ wait.from, wait.until ?? until ]); causes.set( wait.label, list )
				}
				const reasons = [ ...causes ].map( ( [ label, intervals ] )=> ({ label, ms: $shm_hitalama_map_loading_profile_interval( intervals, busy, from, until ) }) ).sort( ( a, b )=> b.ms - a.ms )
				for( let i = 0; i < Math.min( 5, reasons.length ); ++i ) {
					const reason = reasons[i]
					// The key is the operation name, not its changing rank during resumption.
					if( !wait_stage ) break
					const part = wait_stage.step( reason.label, reason.label )
					part.done()
					part.process.data_update({ measured_ms: reason.ms, result: { timing: 'Наблюдаемая причина; интервал без измеренного JS. Может пересекаться с другими причинами. Аргументы и URL не собираются.' } })
				}
				if( finished ) wait_stage?.done( wait_result )
				metric( 'resume-gap', 'После Promise до возобновления · без JS', take( $shm_hitalama_map_loading_profile_interval( resumed, [ ...busy, ...pending ], from, until ) ), { timing: 'От наблюдения готовности Promise до первого обновления связанного fiber. Не чистое время планировщика: часть этого промежутка может быть не нужна для видимого результата.' } )
				metric( 'between', 'Неатрибутированное время', rest, { dropped: this.dropped, timing: 'Остаток после измеренных JS-участков, Promise и наблюдаемого возобновления. Возможны работа вне wire, браузерный layout/paint, отложенный редактор и наблюдение DOM. Это не измерение ожидания.' } )
			}
			for( const [ key, sample ] of this.io.get( widget ) ?? [] ) {
				if( !sample.calls ) continue
				const child = stage.step( 'io-' + key, key === 'units_load' ? 'IndexedDB: чтение и восстановление CRDT · интервал' : 'IndexedDB: чтение файловых блоков · интервал' )
				child.done()
				child.process.data_update({ measured_ms: sample.ms, display_order: 20, result: { calls: sample.calls, timing: 'Сумма интервалов от запуска до готовности Promise, включая продолжение после await и задержку его обработки. Пересекается с общим временем и JS; не складывать с ними. Общий ленд учитывается у первого читателя.' } })
			}
			return total
		}

		stop() {
			this.stopped = true
			if( $shm_hitalama_wire_measure.active === this ) $shm_hitalama_wire_measure.active = undefined
			this.owners = new WeakMap
			this.fibers = new WeakMap
			this.samples.clear()
			this.io.clear()
			this.promises = new WeakSet
			this.waiting = new WeakMap; this.blocked = new WeakMap
			this.waits = []; this.slices = []; this.scope = null
			this.reported = new WeakMap
		}
	}
}
